import type { SiteDownload, SiteDownloadInput, SiteSettingsInput, SitePlatform } from "@player115/contracts";
import { ExternalLink, Save } from "lucide-react";
import { useEffect, useState } from "react";
import { api } from "../api";
import { Status } from "../components/Status";

const platformLabels: Record<SitePlatform, string> = {
  macos: "macOS 版",
  windows: "Windows 版",
  "android-tv": "电视版",
  "android-mobile": "安卓手机版",
};

const defaultSettings: SiteSettingsInput = {
  heroCtaLabel: "即将开放",
  heroCtaHref: "#download",
  heroStatusLabel: "内部测试中",
  downloadTitle: "Five Player，正在准备中",
  downloadDescription: "当前版本仍在内部测试，暂不对外提供下载；按钮仅作预留，不连接下载地址。等播放、画面和稳定性准备好后再正式开放。",
};

function toInput(download: SiteDownload): SiteDownloadInput {
  const { updatedAt: _updatedAt, ...input } = download;
  return input;
}

export function WebsitePage() {
  const [settings, setSettings] = useState<SiteSettingsInput>(defaultSettings);
  const [downloads, setDownloads] = useState<SiteDownload[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    void Promise.all([api.siteSettings(), api.siteDownloads()]).then(([settingsResult, downloadsResult]) => {
      setSettings({ ...settingsResult.settings });
      setDownloads(downloadsResult.downloads);
    }).catch((reason) => setError(reason instanceof Error ? reason.message : "官网发布配置读取失败"));
  }, []);

  const updateSettings = <K extends keyof SiteSettingsInput>(key: K, value: SiteSettingsInput[K]) => {
    setSettings((current) => ({ ...current, [key]: value }));
    setMessage("");
  };
  const updateDownload = <K extends keyof SiteDownloadInput>(platform: SitePlatform, key: K, value: SiteDownloadInput[K]) => {
    setDownloads((current) => current.map((item) => item.platform === platform ? { ...item, [key]: value } : item));
    setMessage("");
  };
  const saveSettings = async () => {
    setBusy("settings");
    setError("");
    try {
      const result = await api.saveSiteSettings(settings);
      setSettings({ ...result.settings });
      setMessage("官网文案已保存");
    } catch (reason) { setError(reason instanceof Error ? reason.message : "官网文案保存失败"); }
    finally { setBusy(null); }
  };
  const saveDownload = async (download: SiteDownload) => {
    setBusy(download.platform);
    setError("");
    try {
      const result = await api.saveSiteDownload(toInput(download));
      setDownloads((current) => current.map((item) => item.platform === download.platform ? result.download : item));
      setMessage(`${platformLabels[download.platform]}发布配置已保存`);
    } catch (reason) { setError(reason instanceof Error ? reason.message : "下载配置保存失败"); }
    finally { setBusy(null); }
  };

  return <section className="page website-page">
    <header className="page-head"><div><h1>官网发布</h1><p>管理 www.5bo.app 的首屏状态与客户端下载入口</p></div><Status tone="success">独立于客户端强升</Status></header>
    {error && <p className="inline-error" role="alert">{error}</p>}
    {message && <p className="inline-success" role="status">{message}</p>}
    <form className="site-publish-editor" onSubmit={(event) => { event.preventDefault(); void saveSettings(); }}>
      <header><div><h2>官网文案</h2><p>控制截图中的首屏按钮、状态和客户端下载区说明。</p></div><button className="primary-button" type="submit" disabled={busy !== null}><Save size={17} />保存文案</button></header>
      <section className="form-section"><h3>首屏入口</h3><div className="form-grid three">
        <label><span>按钮文字</span><input value={settings.heroCtaLabel} onChange={(event) => updateSettings("heroCtaLabel", event.target.value)} /></label>
        <label><span>按钮链接</span><input value={settings.heroCtaHref} onChange={(event) => updateSettings("heroCtaHref", event.target.value)} placeholder="#download 或 https://..." /></label>
        <label><span>状态文字</span><input value={settings.heroStatusLabel} onChange={(event) => updateSettings("heroStatusLabel", event.target.value)} /></label>
      </div></section>
      <section className="form-section"><h3>客户端下载区域</h3><div className="form-grid two">
        <label className="span-two"><span>区域标题</span><input value={settings.downloadTitle} onChange={(event) => updateSettings("downloadTitle", event.target.value)} /></label>
        <label className="span-two"><span>区域说明</span><textarea rows={3} value={settings.downloadDescription} onChange={(event) => updateSettings("downloadDescription", event.target.value)} /></label>
      </div></section>
    </form>
    <section className="site-download-list">
      <header><div><h2>客户端下载</h2><p>这里的发布状态和地址完全独立于客户端版本强升策略。</p></div></header>
      {downloads.map((download) => <form className="site-download-card" key={download.platform} onSubmit={(event) => { event.preventDefault(); void saveDownload(download); }}>
        <header><div><h3>{platformLabels[download.platform]}</h3><small>{download.platform}</small></div><label className="site-publish-toggle"><input type="checkbox" checked={download.published} onChange={(event) => updateDownload(download.platform, "published", event.target.checked)} /><span>发布到官网</span></label></header>
        <div className="form-grid two">
          <label><span>展示版本</span><input value={download.version ?? ""} onChange={(event) => updateDownload(download.platform, "version", event.target.value || null)} placeholder="例如 0.3.0" /></label>
          <label><span>状态文字</span><input value={download.statusLabel} onChange={(event) => updateDownload(download.platform, "statusLabel", event.target.value)} placeholder="暂未开放" /></label>
          <label className="span-two"><span>第三方网盘下载地址</span><div className="input-action"><input type="url" value={download.downloadUrl ?? ""} onChange={(event) => updateDownload(download.platform, "downloadUrl", event.target.value || null)} placeholder="https://..." />{download.downloadUrl && <a href={download.downloadUrl} target="_blank" rel="noreferrer" title="打开链接"><ExternalLink size={17} /></a>}</div></label>
          <label><span>备用地址</span><input type="url" value={download.backupDownloadUrl ?? ""} onChange={(event) => updateDownload(download.platform, "backupDownloadUrl", event.target.value || null)} placeholder="可选" /></label>
          <label><span>提取码</span><input value={download.accessCode ?? ""} onChange={(event) => updateDownload(download.platform, "accessCode", event.target.value || null)} placeholder="可选" /></label>
        </div>
        <footer><span>{download.published && download.downloadUrl ? "官网将显示可下载按钮" : "官网将显示暂未开放"}</span><button className="primary-button" type="submit" disabled={busy !== null}><Save size={17} />保存平台配置</button></footer>
      </form>)}
    </section>
  </section>;
}
